import React, { useState } from 'react';
import axios from 'axios';
import { 
  ChevronLeft, 
  Clock,
  CheckCircle,
  Loader,
  AlertCircle,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

function TimeSlotSelector({ delivery, onBack }) {
  const [selectedSlot, setSelectedSlot] = useState(delivery.slot || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Districts that only have afternoon slots (4 PM - 9 PM)
  const afternoonOnlyDistricts = [
    'Ventanilla',
    'Callao',
    'Villa el salvador',
    'Villa María del triunfo',
    'Chorrillos',
    'Ate',
    'San Juan de Lurigancho',
    'Carabayllo',
    'Puente piedra',
    'Comas',
  ];

  // All available time slots
  const allTimeSlots = [
    { id: 1, label: '2:00 PM - 5:00 PM', start: '14:00', end: '17:00' },
    { id: 2, label: '3:00 PM - 6:00 PM', start: '15:00', end: '18:00' },
    { id: 3, label: '4:00 PM - 7:00 PM', start: '16:00', end: '19:00' },
    { id: 4, label: '5:00 PM - 8:00 PM', start: '17:00', end: '20:00' },
    { id: 5, label: '6:00 PM - 9:00 PM', start: '18:00', end: '21:00' },
  ];

  // Afternoon-only time slots
  const afternoonTimeSlots = [
    { id: 3, label: '4:00 PM - 7:00 PM', start: '16:00', end: '19:00' },
    { id: 4, label: '5:00 PM - 8:00 PM', start: '17:00', end: '20:00' },
    { id: 5, label: '6:00 PM - 9:00 PM', start: '18:00', end: '21:00' },
  ];

  // Determine which slots to show based on district
  const isAfternoonOnlyDistrict = afternoonOnlyDistricts.some(
    district => delivery.district?.toLowerCase().includes(district.toLowerCase())
  );
  
  const timeSlots = isAfternoonOnlyDistrict ? afternoonTimeSlots : allTimeSlots;

  const handleSelectSlot = (slotId) => {
    setSelectedSlot(slotId);
    setError(null);
    setSuccess(false);
  };

  const handleSaveSlot = async () => {
    if (!selectedSlot) {
      setError('Por favor, selecciona un horario');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await axios.post(`${API_URL}/api/delivery_points/${delivery.id}/slot`, {
        slot: selectedSlot,
      });

      setSuccess(true);
      
      // Auto-redirect after success
      setTimeout(() => {
        onBack();
      }, 2000);
    } catch (err) {
      console.error('Error saving slot:', err);
      setError('No se pudo guardar el horario. Por favor, intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-brand-green to-brand-primary shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200 active:scale-95"
              disabled={saving}
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-lg shadow-md">
                <Clock className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-white font-montserrat">
                  Horario de Entrega
                </h1>
                <p className="text-xs text-white/90 font-montserrat">
                  Elige tu ventana de 3 horas
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          
          {/* District Info (if afternoon-only) */}
          {isAfternoonOnlyDistrict && (
            <div className="bg-gradient-to-r from-brand-gold/10 to-brand-primary/10 border border-brand-gold/30 rounded-2xl p-5 mb-6">
              <div className="flex gap-3">
                <Info className="w-6 h-6 text-brand-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-brand-primary font-montserrat mb-2">
                    Horarios para {delivery.district}
                  </h3>
                  <p className="text-sm text-slate-700 font-montserrat">
                    Para tu distrito, ofrecemos entregas en horario de tarde (4:00 PM - 9:00 PM) 
                    para garantizar una mejor logística y servicio.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Instructions Card */}
          <div className="bg-gradient-to-r from-blue-50 to-brand-primary/5 border border-blue-200 rounded-2xl p-5 mb-6">
            <div className="flex gap-3">
              <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-blue-900 font-montserrat mb-2">
                  Instrucciones Importantes
                </h3>
                <ul className="space-y-2 text-sm text-blue-800 font-montserrat">
                  <li className="flex gap-2">
                    <span className="font-bold">•</span>
                    <span>Selecciona una ventana de <strong>3 horas</strong> que te sea conveniente</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold">•</span>
                    <span>
                      {isAfternoonOnlyDistrict 
                        ? 'Para tu distrito, los horarios disponibles son de 4:00 PM a 9:00 PM'
                        : 'Nuestros horarios de entrega son de 2:00 PM a 9:00 PM'
                      }
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold">•</span>
                    <span>El horario puede variar ligeramente debido al tráfico u otros factores</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold">•</span>
                    <span>Te avisaremos cuando el conductor esté cerca</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Current Selection Info */}
          {delivery.slot && !success && (
            <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-2xl p-4 mb-6">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-brand-gold" />
                <div>
                  <p className="text-sm text-slate-700 font-montserrat">
                    Horario actual seleccionado:
                  </p>
                  <p className="font-bold text-brand-primary font-montserrat">
                    {allTimeSlots.find(slot => slot.id === delivery.slot)?.label || 'No definido'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 mb-6 animate-fadeIn">
              <div className="flex gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-green-900 font-montserrat mb-1">
                    ¡Horario guardado exitosamente!
                  </h3>
                  <p className="text-sm text-green-800 font-montserrat">
                    Tu ventana de entrega ha sido actualizada. Regresando al menú principal...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6">
              <div className="flex gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 font-montserrat">{error}</p>
              </div>
            </div>
          )}

          {/* Time Slots Grid */}
          <div className="space-y-3 mb-6">
            <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-4">
              Selecciona tu horario preferido
            </h3>
            
            {timeSlots.map((slot) => {
              const isSelected = selectedSlot === slot.id;
              const isCurrentSlot = delivery.slot === slot.id;
              
              return (
                <button
                  key={slot.id}
                  onClick={() => handleSelectSlot(slot.id)}
                  disabled={saving}
                  className={`w-full rounded-2xl border-2 p-5 transition-all duration-200 ${
                    isSelected
                      ? 'border-brand-primary bg-gradient-to-r from-brand-primary/10 to-brand-gold/10 shadow-md scale-[1.02]'
                      : isCurrentSlot
                      ? 'border-brand-gold bg-brand-gold/5'
                      : 'border-slate-200 bg-white hover:border-brand-primary/50 hover:shadow-sm'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        isSelected
                          ? 'bg-gradient-to-br from-brand-primary to-brand-gold'
                          : isCurrentSlot
                          ? 'bg-brand-gold'
                          : 'bg-slate-100'
                      }`}>
                        <Clock className={`w-6 h-6 ${
                          isSelected || isCurrentSlot ? 'text-white' : 'text-slate-500'
                        }`} />
                      </div>
                      <div className="text-left">
                        <p className={`text-lg font-bold font-montserrat ${
                          isSelected ? 'text-brand-primary' : 'text-slate-900'
                        }`}>
                          {slot.label}
                        </p>
                        <p className="text-sm text-slate-600 font-montserrat">
                          Ventana de 3 horas
                        </p>
                        {isCurrentSlot && !isSelected && (
                          <p className="text-xs text-brand-gold font-montserrat font-semibold mt-1">
                            Horario actual
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {isSelected && (
                      <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Additional Info */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-slate-700 font-montserrat mb-2">
                  <strong>Nota importante:</strong>
                </p>
                <p className="text-sm text-slate-600 font-montserrat">
                  La hora exacta de entrega puede variar dentro de la ventana seleccionada debido a factores como el tráfico, 
                  clima y otras entregas en la ruta. Te mantendremos informado sobre el progreso de tu entrega.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Action Button */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 px-4 py-4 safe-area-bottom">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={handleSaveSlot}
            disabled={!selectedSlot || saving || success}
            className={`w-full flex items-center justify-center gap-2 py-4 px-6 rounded-xl font-bold font-montserrat text-lg transition-all ${
              selectedSlot && !saving && !success
                ? 'bg-gradient-to-r from-brand-primary to-brand-gold text-white hover:shadow-lg active:scale-95'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <Loader className="w-6 h-6 animate-spin" />
                <span>Guardando...</span>
              </>
            ) : success ? (
              <>
                <CheckCircle className="w-6 h-6" />
                <span>Guardado</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-6 h-6" />
                <span>Confirmar Horario</span>
              </>
            )}
          </button>
          
          {selectedSlot && !success && (
            <p className="text-center text-xs text-slate-600 font-montserrat mt-3">
              Has seleccionado: <strong>{timeSlots.find(s => s.id === selectedSlot)?.label}</strong>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default TimeSlotSelector;